import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Download, FileText, TrendingUp, TrendingDown, Scale, Wallet,
  Users, Briefcase, Receipt, Truck, ShoppingCart, Package,
  HardDrive, LifeBuoy, Coins, Contact, Lock, ClipboardCheck,
} from "lucide-react";
import { printCustomerLedgerPdf } from "@/lib/printDoc";
import { buildStatement } from "@/lib/customerStatement";
import { buildSupplierStatement } from "@/lib/supplierStatement";
import { printTable } from "@/lib/dwr";

const fmt = (n) => new Intl.NumberFormat("en-TZ").format(Math.round(n || 0));
const fmtTZS = (n) => `TZS ${fmt(n)}`;

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

const EXPORTS = [
  { key: "customers", label: "Customers", icon: Users },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "suppliers", label: "Suppliers", icon: Truck },
  { key: "purchase_orders", label: "Purchase Orders", icon: ShoppingCart },
  { key: "products", label: "Products", icon: Package },
  { key: "assets", label: "Assets", icon: HardDrive },
  { key: "employees", label: "Employees", icon: Contact },
  { key: "tickets", label: "Tickets", icon: LifeBuoy },
  { key: "expenses", label: "Expenses", icon: TrendingDown },
  { key: "petty_cash", label: "Petty Cash", icon: Coins },
  { key: "leads", label: "Leads", icon: Briefcase },
];

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`font-display text-base font-semibold ${accent || ""}`}>{fmtTZS(value)}</span>
    </div>
  );
}

export default function Reports() {
  const { canViewPage, can } = useAuth();
  const canView = canViewPage("reports");
  const [pl, setPL] = useState(null);
  const [bs, setBS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [ledgerCustomer, setLedgerCustomer] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [supplierBills, setSupplierBills] = useState([]);
  const [ledgerSupplier, setLedgerSupplier] = useState("");
  const canSeeSupplierBills = can("supplier_invoices", "view");
  const [workOrders, setWorkOrders] = useState([]);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    const empty = { data: { items: [] } };
    Promise.all([
      // A 403 (no access to a report's source modules) -> null -> section hidden.
      api.get("/reports/profit-loss").catch(() => ({ data: null })),
      api.get("/reports/balance-sheet").catch(() => ({ data: null })),
      api.get("/customers/", { params: { limit: 500 } }).catch(() => empty),
      api.get("/invoices/", { params: { limit: 1000 } }).catch(() => empty),
      api.get("/work-orders/", { params: { limit: 2000 } }).catch(() => empty),
      // Supplier statements: only for people who may see supplier invoices.
      canSeeSupplierBills ? api.get("/suppliers/", { params: { limit: 500 } }).catch(() => empty) : Promise.resolve(empty),
      canSeeSupplierBills ? api.get("/supplier-invoices/", { params: { limit: 1000 } }).catch(() => empty) : Promise.resolve(empty),
    ])
      .then(([p, b, c, inv, wo, sup, sinv]) => {
        setSuppliers(sup.data.items || []);
        setSupplierBills(sinv.data.items || []);
        setPL(p.data); setBS(b.data);
        setCustomers(c.data.items || []);
        setAllInvoices(inv.data.items || []);
        setWorkOrders(wo.data.items || []);
      })
      .finally(() => setLoading(false));
  }, [canView, canSeeSupplierBills]);

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="module-forbidden">
        <Lock className="w-8 h-8 text-muted-foreground mb-3" />
        <p className="font-display text-lg font-medium">You don't have permission to view this page</p>
      </div>
    );
  }

  const ledgerInvoices = allInvoices
    // A statement of account is about real bills -- proforma quotes aren't
    // a billing commitment yet, so they're excluded here just like they are
    // from every revenue/receivables figure in the system.
    .filter((i) => i.customer === ledgerCustomer && i.doc_type !== "Proforma Invoice");
  const statement = buildStatement(ledgerInvoices);
  const supplierStatement = buildSupplierStatement(supplierBills.filter((b) => b.supplier === ledgerSupplier));
  const printSupplierStatement = () => printTable({
    title: `Supplier statement · ${ledgerSupplier}`,
    subtitle: `Bills received from ${ledgerSupplier}`,
    system: "Accounts Payable",
    summary: [["Billed", fmtTZS(supplierStatement.totals.billed)], ["Paid", fmtTZS(supplierStatement.totals.paid)],
      ["Outstanding", fmtTZS(supplierStatement.totals.outstanding)], ["Overdue", fmtTZS(supplierStatement.totals.overdue)]],
    columns: [
      { label: "Date", value: (e) => e.date }, { label: "Bill No.", value: (e) => e.invoice_no },
      { label: "Due", value: (e) => e.due_date }, { label: "Status", value: (e) => String(e.status).replace(/_/g, " ") },
      { label: "Amount", value: (e) => fmt(e.amount) }, { label: "Still owed", value: (e) => fmt(e.owed) },
      { label: "Balance", value: (e) => fmt(e.balance) },
    ],
    rows: supplierStatement.entries,
  });

  // Grouped by the month the job was scheduled for (falls back to when the
  // record was created if never scheduled), and separately by customer --
  // two different views of the same underlying work order list.
  const monthKey = (wo) => (wo.scheduled_date || wo.created_at || "").slice(0, 7);
  const woByMonth = {};
  const woByCustomer = {};
  for (const wo of workOrders) {
    const mk = monthKey(wo) || "Unscheduled";
    if (!woByMonth[mk]) woByMonth[mk] = { total: 0, completed: 0 };
    woByMonth[mk].total += 1;
    if (wo.status === "completed") woByMonth[mk].completed += 1;

    const ck = wo.customer || "Unassigned";
    if (!woByCustomer[ck]) woByCustomer[ck] = { total: 0, completed: 0, cancelled: 0 };
    woByCustomer[ck].total += 1;
    if (wo.status === "completed") woByCustomer[ck].completed += 1;
    if (wo.status === "cancelled") woByCustomer[ck].cancelled += 1;
  }
  const woMonthRows = Object.entries(woByMonth).sort(([a], [b]) => b.localeCompare(a));
  const woCustomerRows = Object.entries(woByCustomer).sort(([, a], [, b]) => b.total - a.total);

  const download = async (collection, label) => {
    const url = `${API_BASE}/reports/export/${collection}`;
    const token = localStorage.getItem("kn_token");
    const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${collection}.csv`;
    a.click();
  };

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-14 w-64 rounded-xl" />
      <Skeleton className="h-40 rounded-2xl" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4 }}>
        <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Analytics</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Financial reports, statements and exportable analytics.</p>
      </motion.div>

      {/* Hero — Net Profit, same premium treatment as the Dashboard */}
      {pl && (
      <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.05 }}>
        <div className="kn-gradient-border relative p-7 sm:p-8 rounded-2xl border border-border bg-card overflow-hidden" data-testid="net-profit-hero">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gradient-to-br from-blue-500/20 to-amber-500/20 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Net Profit — this period</p>
              <p className="font-display text-4xl sm:text-5xl font-bold mt-3 kn-gradient-text">{fmtTZS(pl?.net_profit)}</p>
              <p className="text-xs text-muted-foreground mt-2">Revenue received minus approved expenses</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs gap-1.5 border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                <TrendingUp className="w-3 h-3" /> Revenue {fmtTZS(pl?.revenue)}
              </Badge>
              <Badge variant="outline" className="text-xs gap-1.5 border-rose-500/30 text-rose-600 dark:text-rose-400">
                <TrendingDown className="w-3 h-3" /> Expenses {fmtTZS(pl?.expenses)}
              </Badge>
              {bs && (
                <Badge variant="outline" className="text-xs gap-1.5 border-amber-500/30 text-amber-600 dark:text-amber-400">
                  <Wallet className="w-3 h-3" /> Receivables {fmtTZS(bs.receivables)}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      )}

      {(pl || bs) && (
      <motion.div
        initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {pl && (
        <Card className="kn-card p-6 rounded-xl border border-border bg-card">
          <SectionHeader icon={TrendingUp} title="Profit & Loss" subtitle="Income statement summary" />
          <Row label="Total Revenue" value={pl?.revenue} accent="text-emerald-500" />
          <Row label="Total Expenses" value={pl?.expenses} accent="text-rose-500" />
          <Row label="Gross Profit" value={pl?.gross_profit} />
          <Row label="Net Profit" value={pl?.net_profit} accent="kn-gradient-text" />
        </Card>
        )}

        {bs && (
        <Card className="kn-card p-6 rounded-xl border border-border bg-card">
          <SectionHeader icon={Scale} title="Balance Sheet" subtitle="Snapshot of financial position" />
          <Row label="Total Assets" value={bs?.assets} />
          <Row label="Receivables" value={bs?.receivables} accent="text-amber-500" />
          <Row label="Payables" value={bs?.payables} accent="text-rose-500" />
        </Card>
        )}
      </motion.div>
      )}

      <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.15 }}>
        <Card className="kn-card p-6 rounded-xl border border-border bg-card" data-testid="customer-ledger-panel">
          <SectionHeader
            icon={FileText}
            title="Customer Ledger"
            subtitle="Statement of account — invoices and payments per customer"
            action={
              <Button
                variant="outline"
                disabled={!ledgerCustomer}
                onClick={() => printCustomerLedgerPdf(ledgerCustomer, ledgerInvoices)}
                data-testid="download-ledger-pdf"
              >
                <FileText className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            }
          />
          <div className="max-w-sm mb-4">
            <SearchableSelect
              value={ledgerCustomer}
              onValueChange={setLedgerCustomer}
              options={customers.map((c) => ({ value: c.name, label: c.name }))}
              placeholder="Select a customer…"
              testId="ledger-customer-select"
            />
          </div>
          {ledgerCustomer && (
            <>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead>Date</TableHead>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Invoiced</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statement.entries.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No invoices for this customer</TableCell></TableRow>
                    ) : statement.entries.map((e) => (
                      <TableRow key={e.key} className={e.kind === "payment" ? "bg-emerald-500/5" : ""} data-testid={`ledger-row-${e.kind}`}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {e.undated
                            ? <span className="text-amber-600" title="Open the invoice and set this installment's date under Payment History">Date not recorded</span>
                            : e.date || "—"}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{e.invoice_no}</TableCell>
                        <TableCell className={`text-xs ${e.kind === "payment" ? "pl-6 text-emerald-700 dark:text-emerald-400" : ""}`}>{e.description}</TableCell>
                        <TableCell className="text-right">{e.debit ? fmt(e.debit) : ""}</TableCell>
                        <TableCell className="text-right">{e.credit ? fmt(e.credit) : ""}</TableCell>
                        <TableCell className="text-right font-semibold">{fmt(e.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-wrap justify-end gap-6 mt-3 text-sm">
                <span>Total Invoiced: <strong>{fmtTZS(statement.totals.invoiced)}</strong></span>
                <span>Total Paid: <strong>{fmtTZS(statement.totals.paid)}</strong></span>
                <span>Outstanding: <strong className="text-amber-600">{fmtTZS(statement.totals.outstanding)}</strong></span>
              </div>
              {statement.undatedCount > 0 && (
                <p className="mt-2 text-xs text-amber-600" data-testid="ledger-undated-note">
                  {statement.undatedCount} payment{statement.undatedCount > 1 ? "s have" : " has"} no recorded date —
                  open the invoice and set the dates under Payment History for a complete statement.
                </p>
              )}
            </>
          )}
        </Card>
      </motion.div>

      {suppliers.length > 0 && (
      <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.16 }}>
        <Card className="kn-card p-6 rounded-xl border border-border bg-card" data-testid="supplier-statement-panel">
          <SectionHeader
            icon={FileText}
            title="Supplier Statement"
            subtitle="Bills from a supplier and what is still owed"
            action={
              <Button variant="outline" disabled={!ledgerSupplier} onClick={printSupplierStatement} data-testid="download-supplier-statement-pdf">
                <FileText className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            }
          />
          <div className="max-w-sm mb-4">
            <SearchableSelect
              value={ledgerSupplier}
              onValueChange={setLedgerSupplier}
              options={suppliers.map((s) => ({ value: s.name, label: s.name }))}
              placeholder="Select a supplier…"
              testId="supplier-statement-select"
            />
          </div>
          {ledgerSupplier && (
            <>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead>Date</TableHead>
                      <TableHead>Bill No.</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Still owed</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supplierStatement.entries.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No bills from this supplier</TableCell></TableRow>
                    ) : supplierStatement.entries.map((e) => (
                      <TableRow key={e.key}>
                        <TableCell className="text-xs whitespace-nowrap">{e.date || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{e.invoice_no}</TableCell>
                        <TableCell className="text-xs">{e.due_date || "—"}</TableCell>
                        <TableCell className="text-xs capitalize">{String(e.status).replace(/_/g, " ")}</TableCell>
                        <TableCell className="text-right">{fmt(e.amount)}</TableCell>
                        <TableCell className="text-right">{e.owed ? fmt(e.owed) : ""}</TableCell>
                        <TableCell className="text-right font-semibold">{fmt(e.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-wrap justify-end gap-6 mt-3 text-sm">
                <span>Billed: <strong>{fmtTZS(supplierStatement.totals.billed)}</strong></span>
                <span>Paid: <strong>{fmtTZS(supplierStatement.totals.paid)}</strong></span>
                <span>Outstanding: <strong className="text-amber-600">{fmtTZS(supplierStatement.totals.outstanding)}</strong></span>
                {supplierStatement.totals.overdue > 0 && <span>Overdue: <strong className="text-rose-600">{fmtTZS(supplierStatement.totals.overdue)}</strong></span>}
              </div>
            </>
          )}
        </Card>
      </motion.div>
      )}

      <motion.div
        initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.18 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <Card className="kn-card p-6 rounded-xl border border-border bg-card" data-testid="work-orders-by-month">
          <SectionHeader icon={ClipboardCheck} title="Work Orders — by Month" subtitle="Jobs scheduled per month, and how many were completed" />
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {woMonthRows.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">No work orders yet</TableCell></TableRow>
                ) : woMonthRows.map(([month, s]) => (
                  <TableRow key={month}>
                    <TableCell className="text-sm font-medium">{month}</TableCell>
                    <TableCell className="text-right">{s.total}</TableCell>
                    <TableCell className="text-right text-emerald-600">{s.completed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <Card className="kn-card p-6 rounded-xl border border-border bg-card" data-testid="work-orders-by-customer">
          <SectionHeader icon={Users} title="Work Orders — by Customer" subtitle="Job volume and completion per customer" />
          <div className="overflow-x-auto rounded-lg border border-border max-h-80 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Completed</TableHead>
                  <TableHead className="text-right">Cancelled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {woCustomerRows.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">No work orders yet</TableCell></TableRow>
                ) : woCustomerRows.map(([customer, s]) => (
                  <TableRow key={customer}>
                    <TableCell className="text-sm font-medium truncate max-w-[200px]">{customer}</TableCell>
                    <TableCell className="text-right">{s.total}</TableCell>
                    <TableCell className="text-right text-emerald-600">{s.completed}</TableCell>
                    <TableCell className="text-right text-rose-500">{s.cancelled}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </motion.div>

      <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.2 }}>
        <Card className="kn-card p-6 rounded-xl border border-border bg-card">
          <SectionHeader icon={Download} title="Quick Exports" subtitle="Download any module's records as CSV" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {EXPORTS.filter(({ key }) => can(key, "view")).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => download(key, label)}
                data-testid={`export-${key}`}
                className="flex items-center gap-3 p-4 rounded-xl border border-border bg-background kn-card transition-all hover:translate-y-[-1px] hover:shadow-md text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{label}</p>
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Download className="w-3 h-3" /> CSV
                  </p>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
